import React, { useState, useEffect } from "react";
import { Autocomplete, TextField, CircularProgress, InputAdornment } from "@mui/material";
import { Option } from "../Types/CommonTypes";
import InfoButton from "./Tooltip/InfoTextField";
interface AsyncAutoCompleteMultiSelectProps {
    label: string;
    fetchOptions: (search: string, offset: number, code?: string) => Promise<Option[]>; // Fetch function with optional code
    onChange: (value: Option[]) => void; // Change handler for multiselect
    value?: Option[]; // Selected values for multiselect
    initialOptions?: Option[]; // Preloaded options from the parent
    disabled?: boolean | null;
    infoText?: string; // info text 
    error?: boolean;  //error prop
    helperText?: string; // helperText prop
}

const AsyncAutoCompleteMultiSelect: React.FC<AsyncAutoCompleteMultiSelectProps> = ({
    label,
    fetchOptions,
    onChange,
    value = [],
    initialOptions = [],
    disabled = false,
    infoText,
    error,
    helperText
}) => {
    const [options, setOptions] = useState<Option[]>(initialOptions); // Initialize with passed options
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState(""); // For tracking search input
    const [offset, setOffset] = useState(0); // Pagination offset
    const [hasMore, setHasMore] = useState(true); // Track if more data can be fetched


    useEffect(() => {
        setOptions((prev) => {
            const combined = [...prev];

            for (const opt of initialOptions) {
                if (!combined.some(o => o.value === opt.value)) {
                    combined.push(opt);
                }
            }

            return combined;
        });
    }, [initialOptions]);
    // Fetch options on input change
    useEffect(() => {
        const loadOptions = async () => {
            if (inputValue.trim() === "" && value.length === 0) {
                // If input is empty and no values selected, fetch initial options
                setLoading(true);
                try {
                    const defaultOptions = initialOptions;
                    setOptions(defaultOptions);
                    setOffset(defaultOptions.length);
                    setHasMore(defaultOptions.length > 0); // Disable further fetches if no results
                } catch (error) {
                    console.error("Error loading default options:", error);
                } finally {
                    setLoading(false);
                }
                return;
            }

            if (inputValue.trim() === "") return; // Skip if input is empty

            setLoading(true);
            try {
                const newOptions = await fetchOptions(inputValue, 0);
                setOptions(newOptions);
                setOffset(newOptions.length);
                setHasMore(newOptions.length > 0); // Disable further fetches if no results
            } catch (error) {
                console.error("Error loading options:", error);
            } finally {
                setLoading(false);
            }
        };

        loadOptions();
    }, [inputValue, fetchOptions, value]);

    const handleScroll = async (event: React.SyntheticEvent) => {
        const listboxNode = event.currentTarget;
        if (
            listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight &&
            hasMore &&
            !loading
        ) {
            setLoading(true);
            try {
                const newOptions = await fetchOptions(inputValue, offset);
                setOptions((prev) => [...prev, ...newOptions]);
                setOffset((prev) => prev + newOptions.length);
                setHasMore(newOptions.length > 0);
            } catch (error) {
                console.error("Error fetching more options:", error);
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <Autocomplete
            multiple // Enable multiselect
            options={options}
            getOptionLabel={(option) => option.description || ""}
            value={value} // Value is an array for multiselect
            clearOnEscape
            clearIcon={false}
            onChange={(event, newValue) => onChange(newValue as Option[])}
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            loading={loading}
            disabled={disabled || false}
            ListboxProps={{
                onScroll: handleScroll,
                style: { maxHeight: 300, overflow: "auto" }, // Ensure scrolling is enabled
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    error={error}
                    helperText={helperText} 
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>


                                {params.InputProps.endAdornment}
                                {infoText && (
                                    <InputAdornment position="end">
                                        <InfoButton infoText={infoText} />
                                    </InputAdornment>
                                )}
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                            </>
                        ),
                        autoComplete: 'new-password', // disable autocomplete and autofill
                    }}
                />
            )}
        />
    );
};

export default AsyncAutoCompleteMultiSelect;
